// src/utils/date.ts
//
// KST(Asia/Seoul) 고정 날짜 유틸.
//
// 왜 별도 모듈인가:
//  - 컨테이너 임대 타임라인은 "오늘선"과 "연체" 판정이 전부 KST 벽시계 기준이다.
//    브라우저 TZ(UTC, PST 등)가 무엇이든 동일한 결과가 나와야 한다.
//  - dayjs 번들 공식 플러그인 `dayjs/plugin/utc` 의 `utcOffset(540)` 만 사용한다.
//    (package.json 의 서드파티 `dayjs-plugin-utc` 는 사용하지 않는다.)
//    timezone 플러그인은 Intl 데이터에 의존하므로 여기서는 쓰지 않는다 —
//    KST 는 DST 가 없는 고정 +09:00 이라 오프셋 고정으로 충분하고 더 안전하다.
//
// 기존 src/utils/dateFormat.ts 는 문자열 슬라이스 기반 표시 포맷터로 그대로 둔다.
// 이 모듈은 "계산이 필요한" 날짜(타임라인 스케일, 연체 판정)를 담당한다.

import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/** KST 고정 오프셋(분). Asia/Seoul 은 1988 년 이후 DST 가 없다. */
export const KST_OFFSET_MINUTES = 9 * 60;

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * 주어진 dayjs 를 +09:00 표시로 고정한다(순간(instant)은 바뀌지 않음).
 *
 * 반드시 멱등이어야 한다 — dayjs 의 utcOffset() 은 이미 오프셋이 적용된
 * 인스턴스에 다시 걸면 벽시계가 한 번 더 밀린다(1.11 확인). 이 유틸은 여러
 * 계층에서 겹쳐 호출되므로 현재 오프셋이 이미 +09:00 이면 그대로 돌려준다.
 */
export const toKst = (d: Dayjs): Dayjs =>
  d.utcOffset() === KST_OFFSET_MINUTES ? d : d.utcOffset(KST_OFFSET_MINUTES);

/**
 * 지금 이 순간을 KST 벽시계로 표현한 dayjs.
 * 브라우저 TZ 와 무관하게 `.year()/.month()/.date()` 가 한국 날짜를 돌려준다.
 */
export const nowKst = (): Dayjs => toKst(dayjs());

/** 오늘(KST) 자정. 타임라인의 오늘선·연체 비교 기준. */
export const todayKst = (): Dayjs => nowKst().startOf("day");

/**
 * 서버가 준 RFC3339 문자열(또는 "YYYY-MM-DD")을 +09:00 고정 dayjs 로 변환.
 *
 * - RFC3339(오프셋 포함): 순간을 정확히 파싱한 뒤 KST 표시로 전환.
 * - "YYYY-MM-DD"(날짜만): 브라우저 로컬 자정으로 해석되는 것을 막기 위해
 *   명시적으로 `T00:00:00+09:00` 을 붙여 KST 자정으로 고정.
 *
 * 파싱 불가/빈 값이면 null.
 */
export function parseKst(value: string | null | undefined): Dayjs | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnly = DATE_ONLY_RE.exec(raw);
  const normalized = dateOnly ? `${raw}T00:00:00+09:00` : raw;

  const d = dayjs(normalized);
  if (!d.isValid()) return null;
  return toKst(d);
}

/** parseKst 와 동일하되 실패 시 KST 오늘 자정으로 폴백 — 렌더 경로에서 사용. */
export function parseKstOr(
  value: string | null | undefined,
  fallback: Dayjs
): Dayjs {
  return parseKst(value) ?? fallback;
}

type DateLike = Dayjs | string | Date | null | undefined;

function coerce(value: DateLike): Dayjs | null {
  if (value == null) return null;
  if (typeof value === "string") return parseKst(value);
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? toKst(d) : null;
  }
  // Dayjs
  return value.isValid() ? toKst(value) : null;
}

/** "YYYY.MM.DD" (KST). 실패 시 em-dash. */
export function fmtDate(value: DateLike): string {
  const d = coerce(value);
  return d ? d.format("YYYY.MM.DD") : "—";
}

/** "YY.MM.DD" (KST). 실패 시 em-dash. */
export function fmtDateShort(value: DateLike): string {
  const d = coerce(value);
  return d ? d.format("YY.MM.DD") : "—";
}

/** "YYYY-MM-DD" (KST). 서버 파라미터용. 실패 시 빈 문자열. */
export function toIsoDate(value: DateLike): string {
  const d = coerce(value);
  return d ? d.format("YYYY-MM-DD") : "";
}
